import React from 'react';
import { motion } from 'framer-motion';
import { Download, Smartphone, Zap, Eye } from 'lucide-react';

const Hero = () => {
  return (
    <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium"
              >
                <Zap className="w-4 h-4 mr-2" />
                Modern PDF Experience
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-secondary-900 leading-tight"
              >
                Powerful PDF Viewer for{' '}
                <span className="text-gradient">Modern Devices</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-xl text-secondary-600 leading-relaxed"
              >
                Experience the future of document reading with our advanced PDF viewer. 
                Built with cutting-edge technology and intuitive design for seamless document handling.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a 
                href="https://play.google.com/store/apps/details?id=com.palru.pdfviewer&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center justify-center"
              >
                <Download className="w-5 h-5 mr-2" />
                Download on Google Play
              </a>
              <a 
                href="#demo"
                className="btn-secondary flex items-center justify-center"
              >
                <Eye className="w-5 h-5 mr-2" />
                Try Demo
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="flex items-center space-x-6 text-sm text-secondary-500"
            >
              <div className="flex items-center">
                <Smartphone className="w-4 h-4 mr-2" />
                Android 6.0+
              </div>
              <div className="flex items-center">
                <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="PDF" className="w-4 h-4 mr-2 rounded" />
                All PDF Formats
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Device Mockup */}
              <div className="relative mx-auto w-80 h-96 bg-gradient-to-br from-secondary-800 to-secondary-900 rounded-3xl shadow-2xl transform rotate-3">
                <div className="absolute inset-2 bg-white rounded-2xl overflow-hidden">
                  <div className="h-full bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                                         <div className="text-center space-y-4">
                       <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center mx-auto">
                         <img 
                           src={process.env.PUBLIC_URL + '/favicon.png'} 
                           alt="PDF Viewer" 
                           className="w-8 h-8 rounded-lg"
                           onError={(e) => {
                             console.log('Image failed to load:', e.target.src);
                             e.target.style.display = 'none';
                           }}
                         />
                       </div>
                       <div className="space-y-2">
                         <div className="h-2 bg-primary-200 rounded w-32 mx-auto"></div>
                         <div className="h-2 bg-primary-200 rounded w-24 mx-auto"></div>
                         <div className="h-2 bg-primary-200 rounded w-28 mx-auto"></div>
                       </div>
                     </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl shadow-lg flex items-center justify-center"
              >
                <Zap className="w-8 h-8 text-white" />
              </motion.div>

              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-xl shadow-lg flex items-center justify-center"
              >
                <Eye className="w-6 h-6 text-white" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 